import { Box, Button, Card, CardBody, Flex, Heading, Text, Image } from '@chakra-ui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChakraNextResponsiveImage } from '@/components/ui/shared/image/ChakraNextResponsiveImage';
import { NextLibLynxLoginLink } from '@/components/ui/shared/utilities/NextLibLynxLoginLink/NextLibLynxLoginLink';
import useSession from '@/hooks/useSession';

interface ILectureOverlay {
  posterUrl: string;
}

export const PREMIUM_OVERLAY_ID = 'restricted-access';

export const EpisodePremiumOverlayDesktop: FC<ILectureOverlay> = ({ posterUrl }) => {
  const [descriptionText, setDescriptionText] = useState<string | null>(null);
  const { t } = useTranslation();
  const router = useRouter();
  const { session } = useSession();

  const onMouseHover = (text: string) => {
    setDescriptionText(text);
  };

  return (
    <Flex id={PREMIUM_OVERLAY_ID} w="full" h="full" hideBelow="lg" position="relative">
      <ChakraNextResponsiveImage
        filter="brightness(60%)"
        w="full"
        maxW="1200px"
        h="675px"
        src={posterUrl}
        alt="Lecture image placeholder"
      />
      <Card
        position="absolute"
        left="50%"
        top="50%"
        transform="translate(-50%, -50%)"
        variant="glass"
        maxW={{ base: '331px', md: '592px' }}
        w="full"
      >
        <CardBody py={{ base: 4, md: 12, lg: 20 }} px={{ base: 6, md: 20 }}>
          <Flex color="white" mx="auto" h="full" w="full" flexDir="column" alignItems="center">
            <Heading fontSize={{ base: 'm', md: '3xl' }}>{t('premiumContentFallback.title')}</Heading>
            <Text
              textAlign={'center'}
              mb={{ base: 4, md: 4 }}
              fontSize={{ base: 'xs', md: 'md' }}
              mt={{ base: 1, sm: 2, lg: 4 }}
            >
              {t('premiumContentFallback.description')}
            </Text>
            <Box
              textAlign={'center'}
              w={'100%'}
              bg={descriptionText ? 'rgba(255, 255, 255, 0.2)' : 'transparent'}
              h={{ base: 16, md: 12 }}
              borderRadius="md"
              px={2}
              py={1}
              fontSize={'12px'}
              mb={3}
            >
              {descriptionText}
            </Box>

            <Flex maxW={{ base: '227px', md: 'full' }} flexDir="column" w="full">
              <Button
                onMouseEnter={() => onMouseHover(t('premiumContentFallback.getFullAccess'))}
                onMouseLeave={() => onMouseHover(null)}
                w="full"
                as="a"
                href="https://underline-sub.paperform.co/"
                size={{ base: 'xs', md: 'lg' }}
              >
                {t('premiumContentFallback.monthlySubscription')}
              </Button>
              <Button
                onMouseEnter={() => onMouseHover(t('premiumContentFallback.payPerView'))}
                onMouseLeave={() => onMouseHover(null)}
                href="https://underlineppw.paperform.co/"
                as={NextLink}
                w="full"
                variant="solid"
                mt="2"
                size={{ base: 'xs', md: 'lg' }}
              >
                {t('premiumContentFallback.payPerViewButton')}
              </Button>
              <NextLibLynxLoginLink passHref>
                <Button
                  onMouseEnter={() => onMouseHover(t('premiumContentFallback.accessWithInstitution'))}
                  onMouseLeave={() => onMouseHover(null)}
                  w="full"
                  mt={2}
                  variant="solid"
                  colorScheme="black"
                  as="a"
                  size={{ base: 'xs', md: 'lg' }}
                >
                  {t('liblynx.accessThroughYourInstitution')}
                </Button>
              </NextLibLynxLoginLink>
              {!session && (
                <NextLink href={`/log-in?redirectUrl=${router.asPath}`} passHref legacyBehavior>
                  <Button
                    onMouseEnter={() => onMouseHover(t('premiumContentFallback.alreadyHaveAnAccount'))}
                    onMouseLeave={() => onMouseHover(null)}
                    mt={2}
                    w="full"
                    as="a"
                    colorScheme="white"
                    size={{ base: 'xs', md: 'lg' }}
                  >
                    {t('premiumContentFallback.loginWithUnderline')}
                  </Button>
                </NextLink>
              )}
            </Flex>
            <Flex flexDir={'row'} textAlign={'center'} mt={4}>
              <Box fontSize={{ base: 'xs', md: 'md' }} display={'flex'} alignItems={'center'}>
                Need help?
              </Box>
              <NextLink href={`/contact-us/support`} passHref legacyBehavior>
                <Button
                  fontSize={{ base: 'md' }}
                  mt={2}
                  as="a"
                  size={{ base: 'xs', md: 'lg' }}
                  bg="transparent"
                  _hover={{ bg: 'transparent' }}
                  p={0}
                  m={0}
                  rightIcon={<Image boxSize={5} mr={2.5} src={'/images/help-desk.svg'} />}
                >
                  Contact us
                </Button>
              </NextLink>
            </Flex>
          </Flex>
        </CardBody>
      </Card>
    </Flex>
  );
};
